import React, { useEffect } from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  Button,
  useUpdate,
  useNotify,
  useRefresh,
  useRecordContext,
  Filter,
  SelectInput,
  SearchInput,
  TopToolbar,
  ExportButton,
  CreateButton,
} from "react-admin";
import { Chip } from "@mui/material";

// Фильтры для списка уведомлений
const NotificationFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="title" alwaysOn />
    <SelectInput
      source="status"
      choices={[
        { id: "pending", name: "Ожидает" },
        { id: "sent", name: "Отправлено" },
        { id: "failed", name: "Ошибка" },
        { id: "retrying", name: "Повтор" },
      ]}
      alwaysOn
    />
    <SelectInput
      source="type"
      choices={[
        { id: "welcome", name: "Добро пожаловать" },
        { id: "password_change", name: "Смена пароля" },
        { id: "password_reset", name: "Сброс пароля" },
        { id: "follow", name: "Подписка" },
        { id: "invite", name: "Приглашение" },
        { id: "checkin_reminder", name: "Напоминание о чекине" },
        { id: "challenge_start", name: "Начало челленджа" },
        { id: "challenge_complete", name: "Завершение челленджа" },
        { id: "supplement_order_confirmed", name: "Подтверждение заказа" },
        { id: "payment_success", name: "Успешная оплата" },
        { id: "payment_failed", name: "Ошибка оплаты" },
      ]}
    />
    <SelectInput
      source="channel"
      choices={[
        { id: "email", name: "Email" },
        { id: "push", name: "Push" },
        { id: "sms", name: "SMS" },
      ]}
    />
  </Filter>
);

// Действия в заголовке списка
const ListActions = () => {
  const refresh = useRefresh();

  return (
    <TopToolbar>
      <CreateButton />
      <Button label="Обновить" onClick={refresh} startIcon={<span>🔄</span>} />
      <ExportButton />
    </TopToolbar>
  );
};

// Компонент для отображения статуса с цветом
const StatusChip = () => {
  const record = useRecordContext();
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "sent":
        return "success";
      case "failed":
        return "error";
      case "retrying":
        return "warning";
      case "pending":
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case "sent":
        return "Отправлено";
      case "failed":
        return "Ошибка";
      case "retrying":
        return "Повтор";
      case "pending":
      default:
        return "Ожидает";
    }
  };

  if (!record) return null;

  return (
    <Chip
      label={getStatusLabel(record.status)}
      color={getStatusColor(record.status) as any}
      size="small"
    />
  );
};

// Компонент для отображения типа уведомления
const TypeChip = () => {
  const record = useRecordContext();
  const getTypeLabel = (type: string) => {
    switch (type) {
      case "welcome":
        return "Добро пожаловать";
      case "password_change":
        return "Смена пароля";
      case "password_reset":
        return "Сброс пароля";
      case "follow":
        return "Подписка";
      case "invite":
        return "Приглашение";
      case "checkin_reminder":
        return "Напоминание о чекине";
      case "challenge_start":
        return "Начало челленджа";
      case "challenge_complete":
        return "Завершение челленджа";
      case "supplement_order_confirmed":
        return "Подтверждение заказа";
      case "payment_success":
        return "Успешная оплата";
      case "payment_failed":
        return "Ошибка оплаты";
      default:
        return type;
    }
  };

  if (!record) return null;

  return (
    <Chip label={getTypeLabel(record.type)} variant="outlined" size="small" />
  );
};

// Компонент для отображения канала
const ChannelChip = () => {
  const record = useRecordContext();
  const getChannelColor = (channel: string) => {
    switch (channel) {
      case "email":
        return "primary";
      case "push":
        return "secondary";
      case "sms":
        return "info";
      default:
        return "default";
    }
  };

  if (!record) return null;

  return (
    <Chip
      label={record.channel.toUpperCase()}
      color={getChannelColor(record.channel) as any}
      size="small"
    />
  );
};

// Кнопка для повторной отправки
const RetryButton = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleRetry = async () => {
    try {
      await update("notifications", {
        id: record.id,
        data: { action: "retry" },
      });
      notify("Уведомление добавлено в очередь для повторной отправки", {
        type: "success",
      });
      refresh();
    } catch (error) {
      notify("Ошибка при повторной отправке уведомления", { type: "error" });
    }
  };

  if (record.status?.toLowerCase() !== "failed") {
    return null;
  }

  return (
    <Button
      label="Повторить"
      onClick={handleRetry}
      disabled={isLoading}
      size="small"
    />
  );
};

export const NotificationList = () => {
  const refresh = useRefresh();

  // Автоматическое обновление каждые 30 секунд
  useEffect(() => {
    const interval = setInterval(() => {
      refresh();
    }, 30000); // 30 секунд

    return () => clearInterval(interval);
  }, [refresh]);

  return (
    <List
      title="Уведомления"
      filters={<NotificationFilter />}
      actions={<ListActions />}
      perPage={25}
      sort={{ field: "createdAt", order: "DESC" }}
    >
      <Datagrid rowClick="show">
        <TextField source="id" label="ID" />
        <StatusChip />
        <TypeChip />
        <ChannelChip />
        <TextField source="title" label="Заголовок" />
        <TextField source="recipient" label="Получатель" />
        <FunctionField
          label="Пользователь"
          render={(record: any) =>
            record.user
              ? `${record.user.username} (${record.user.email})`
              : "Система"
          }
        />
        <TextField source="retryCount" label="Попытки" />
        <DateField source="createdAt" label="Создано" showTime />
        <DateField source="sentAt" label="Отправлено" showTime />
        <RetryButton />
      </Datagrid>
    </List>
  );
};
